/**
 * React Query hooks for acquiring and releasing page-level locks.
 *
 */

import useApiClients from '@hooks/useApiClients.tsx';
import {
  LockEvent,
  LockPageMutation,
  LockPageMutationVariables,
  ReleasePageLockMutation,
  ReleasePageLockMutationVariables,
} from '@services/api/deckManagement/graphql/schema/API';
import { lockPage, releasePageLock } from '@services/api/deckManagement/graphql/schema/mutations';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import {
  createQueryOrMutationKey,
  createStandardMutationOptions,
  StandardMutationOptions,
} from '@utils/reactQueryConfig.ts';
import { omit } from 'lodash';

import { decksQueryKeys } from './useAllDecks';
import { deckQueryKeys } from './useDeck';
import { pageQueryKeys } from './usePage';

// Mutation keys for page-lock modifications
export const pageLockMutationKeys = {
  all: createQueryOrMutationKey('page', 'lock'),
  lockPage: () => createQueryOrMutationKey(...pageLockMutationKeys.all, 'lockPage'),
  releasePageLock: () => createQueryOrMutationKey(...pageLockMutationKeys.all, 'releasePageLock'),
};

/**
 * Interface for page lock/release operation parameters.
 * @param deckId - The unique identifier for the deck the page belongs to
 * @param pageName - The logical page name (locks all its grain variants together)
 * @param successCallback - Optional callback to execute on success
 * @interface
 */
export interface UsePageLockParams {
  deckId: string;
  pageName: string;
  successCallback?: () => void;
}

/**
 * Invalidate the caches affected by a page lock change: the specific page's grain
 * queries plus the deck and deck-list queries (deck reads expose page-lock state
 * for mutual-exclusion, and the deck-list surfaces lockable status).
 */
const invalidatePageLockQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  deckId: string,
  pageName: string,
) => {
  queryClient.invalidateQueries({ queryKey: pageQueryKeys.getPagesByPageName(pageName, deckId) });
  queryClient.invalidateQueries({ queryKey: deckQueryKeys.deck(deckId) });
  queryClient.invalidateQueries({ queryKey: decksQueryKeys.list() });
};

/**
 * Hook for acquiring a page lock (all grain variants of the page).
 * @param options - Additional Hook-Level mutation options
 * @returns UseMutationResult resolving to the resulting LockEvent
 */
export const useLockPage = (
  options?: StandardMutationOptions<LockEvent, Error, UsePageLockParams>,
): UseMutationResult<LockEvent, Error, UsePageLockParams> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: pageLockMutationKeys.lockPage(),
    mutationFn: async (params: UsePageLockParams): Promise<LockEvent> => {
      const { deckId, pageName } = params;

      if (!deckId) {
        throw new Error('Deck ID is required');
      }
      if (!pageName) {
        throw new Error('Page Name is required');
      }

      const variables: LockPageMutationVariables = {
        deck_id: deckId,
        page_name: pageName,
      };

      const result = await deckManagementClient.graphql({
        query: lockPage,
        variables,
      });

      if ('data' in result && (result.data as LockPageMutation)?.lockPage) {
        return (result.data as LockPageMutation).lockPage as LockEvent;
      }

      throw new Error('Failed to LOCK page');
    },
    ...createStandardMutationOptions<LockEvent, Error, UsePageLockParams>({
      ...omit(options, 'onSuccess'),
      retryBypassExceptions: ['VALIDATION_ERROR', 'LOCK_CONFLICT', 'BATCH_OPERATION_ERROR'],
      rumOp: extractGraphqlOperationName(lockPage),
    }),
    onSuccess(data, variables) {
      const { deckId, pageName, successCallback: mutateSuccessCallback } = variables;

      invalidatePageLockQueries(queryClient, deckId, pageName);

      if (options?.onSuccess) {
        options.onSuccess(data, variables);
      }
      if (mutateSuccessCallback) mutateSuccessCallback();
    },
  });
};

/**
 * Hook for releasing a page lock (all grain variants of the page).
 * @param options - Additional Hook-Level mutation options
 * @returns UseMutationResult resolving to the resulting LockEvent
 */
export const useReleasePageLock = (
  options?: StandardMutationOptions<LockEvent, Error, UsePageLockParams>,
): UseMutationResult<LockEvent, Error, UsePageLockParams> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: pageLockMutationKeys.releasePageLock(),
    mutationFn: async (params: UsePageLockParams): Promise<LockEvent> => {
      const { deckId, pageName } = params;

      if (!deckId) {
        throw new Error('Deck ID is required');
      }
      if (!pageName) {
        throw new Error('Page Name is required');
      }

      const variables: ReleasePageLockMutationVariables = {
        deck_id: deckId,
        page_name: pageName,
      };

      const result = await deckManagementClient.graphql({
        query: releasePageLock,
        variables,
      });

      if ('data' in result && (result.data as ReleasePageLockMutation)?.releasePageLock) {
        return (result.data as ReleasePageLockMutation).releasePageLock as LockEvent;
      }

      throw new Error('Failed to RELEASE page lock');
    },
    ...createStandardMutationOptions<LockEvent, Error, UsePageLockParams>({
      ...omit(options, 'onSuccess'),
      retryBypassExceptions: ['VALIDATION_ERROR', 'LOCK_CONFLICT', 'BATCH_OPERATION_ERROR'],
      rumOp: extractGraphqlOperationName(releasePageLock),
    }),
    onSuccess(data, variables) {
      const { deckId, pageName, successCallback: mutateSuccessCallback } = variables;

      invalidatePageLockQueries(queryClient, deckId, pageName);

      if (options?.onSuccess) {
        options.onSuccess(data, variables);
      }
      if (mutateSuccessCallback) mutateSuccessCallback();
    },
  });
};
